import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Form, InputGroup, Table } from "react-bootstrap";
import { Link, useFetcher } from "react-router";
import ConfirmCancelDialog from "components/shared/ConfirmDialog";

const MAX_ACTIVE_TOKENS = 5;
const STATUS_VARIANT = {
    active: "success",
    revoked: "danger",
    expired: "secondary",
};

const DATE_FORMAT = { dateStyle: "medium", timeStyle: "short" };
const formatDateTime = (value) =>
    new Date(value).toLocaleString(undefined, DATE_FORMAT);
// short zone name of the viewer (e.g. "PDT", "GMT+8") so the column headers say which timezone applies
const getTimeZoneName = () =>
    new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
        .formatToParts(new Date())
        .find((part) => part.type === "timeZoneName")?.value;

const McpTokenSection = ({ tokens = [] }) => {
    const createFetcher = useFetcher();
    const revokeFetcher = useFetcher();
    const [newToken, setNewToken] = useState(null);
    const [revokeTarget, setRevokeTarget] = useState(null);
    const [name, setName] = useState("");
    const [error, setError] = useState(null);
    const busy =
        createFetcher.state !== "idle" || revokeFetcher.state !== "idle";
    const timeZoneName = getTimeZoneName();
    const activeCount = tokens.filter((t) => t.status === "active").length;
    const limitReached = activeCount >= MAX_ACTIVE_TOKENS;

    useEffect(() => {
        const result = createFetcher.data;
        if (result?.token) {
            setNewToken(result.token);
            setName("");
        } else if (result?.error) {
            setError(result.error);
        }
    }, [createFetcher.data]);

    const onGenerate = () => {
        setError(null);
        createFetcher.submit(
            { action: "create_mcp_token", name: name.trim() },
            { method: "post", encType: "application/json" }
        );
    };

    const onRevokeDialog = (event) => {
        const target = revokeTarget;
        setRevokeTarget(null);
        if (event !== "confirm") {
            return;
        }
        revokeFetcher.submit(
            { action: "revoke_mcp_token", id: target.id },
            { method: "post", encType: "application/json" }
        );
    };

    return (
        <>
            <h5>MCP Access Tokens</h5>
            <hr />
            <p>
                Tokens let AI tools (e.g. OpenClaw, VS Code) access your
                wishlist through MCP. See the{" "}
                <Link to="/help/mcp">setup guide</Link>. Revoke a token
                immediately if you think it has leaked.
            </p>
            {newToken && (
                <Alert
                    variant="warning"
                    dismissible
                    onClose={() => setNewToken(null)}
                    data-testid="alert_new_mcp_token"
                >
                    <strong>Copy it now, it won&apos;t be shown again.</strong>
                    <InputGroup className="mt-2">
                        <Form.Control
                            readOnly
                            value={newToken}
                            aria-label="New MCP token"
                            onFocus={(e) => e.target.select()}
                        />
                        <Button
                            variant="outline-secondary"
                            onClick={() =>
                                navigator.clipboard?.writeText(newToken)
                            }
                        >
                            Copy
                        </Button>
                    </InputGroup>
                </Alert>
            )}
            {tokens.length > 0 && (
                <Table responsive size="sm" data-testid="table_mcp_tokens">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Status</th>
                            <th>Issued ({timeZoneName})</th>
                            <th>Expires ({timeZoneName})</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {tokens.map((t) => (
                            <tr key={t.id}>
                                <td>{t.name}</td>
                                <td>
                                    <Badge bg={STATUS_VARIANT[t.status]}>
                                        {t.status}
                                    </Badge>
                                </td>
                                <td>{formatDateTime(t.issued_at)}</td>
                                <td>{formatDateTime(t.expires_at)}</td>
                                <td>
                                    {t.status === "active" && (
                                        <Button
                                            size="sm"
                                            variant="danger"
                                            disabled={busy}
                                            onClick={() => setRevokeTarget(t)}
                                        >
                                            Revoke
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
            <Form
                className="mb-3"
                onSubmit={(e) => {
                    e.preventDefault();
                    onGenerate();
                }}
            >
                <InputGroup>
                    <Form.Control
                        value={name}
                        onChange={(e) => {
                            setName(e.target.value);
                            setError(null);
                        }}
                        maxLength={100}
                        placeholder="Token name, e.g. OpenClaw or VS Code"
                        aria-label="Token name"
                        disabled={busy || limitReached}
                    />
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={busy || limitReached || !name.trim()}
                    >
                        Generate new token
                    </Button>
                </InputGroup>
                {error && (
                    <Alert
                        variant="danger"
                        className="mt-2 mb-0"
                        data-testid="alert_mcp_token_error"
                    >
                        {error}
                    </Alert>
                )}
                <Form.Text>
                    {limitReached
                        ? `You have reached the limit of ${MAX_ACTIVE_TOKENS} active tokens. Revoke one to create another.`
                        : `${activeCount} of ${MAX_ACTIVE_TOKENS} active tokens used.`}
                </Form.Text>
            </Form>
            <ConfirmCancelDialog
                show={revokeTarget !== null}
                title="Confirm Revoke Token"
                callback={onRevokeDialog}
            >
                <p>
                    Revoke this token? Any tool using it will stop working
                    immediately. This cannot be undone.
                </p>
            </ConfirmCancelDialog>
        </>
    );
};

export default McpTokenSection;
